<script setup>
import { useAbility } from '@casl/vue'

import { cookieRef } from '@/@layouts/stores/config'

import DocumentTypeService from '@/services/quality/documentType.service'
import { documentTierTitle } from '@/enums/quality/document-tier'
import { reviewFrequencyTitle } from '@/enums/quality/review-frequency'
import DocumentTypeDrawer from '@/views/quality/document/masters/DocumentTypeDrawer.vue'

definePage({
  meta: {
    name: 'app-quality-document-masters-types',
    subject: 'document.menu',
    action: 'masters',
  },
})

const { can } = useAbility()
const selectedUnitId = cookieRef('selectedUnit')

const types = ref([])
const isFetching = ref(false)
const isDrawerOpen = ref(false)
const editing = ref(null)
const isConfirmOpen = ref(false)
const deleting = ref(null)

const fetchTypes = async () => {
  isFetching.value = true
  await DocumentTypeService.list({ unit_id: selectedUnitId.value }, data => {
    types.value = data?.data ?? data ?? []
  })
  isFetching.value = false
}

fetchTypes()

const openCreate = () => {
  editing.value = null
  isDrawerOpen.value = true
}

const openEdit = type => {
  editing.value = type
  isDrawerOpen.value = true
}

const confirmDelete = type => {
  deleting.value = type
  isConfirmOpen.value = true
}

const doDelete = async () => {
  await DocumentTypeService.destroy(deleting.value.id, () => {
    isConfirmOpen.value = false
    fetchTypes()
  }, () => {
    isConfirmOpen.value = false
  })
}
</script>

<template>
  <div>
    <div class="d-flex align-center mb-6">
      <div class="text-h5 flex-grow-1">
        Document Types
      </div>
      <VBtn
        v-if="can('configure', 'quality.document')"
        prepend-icon="tabler-plus"
        @click="openCreate"
      >
        New type
      </VBtn>
    </div>

    <VProgressLinear
      v-if="isFetching"
      indeterminate
      color="primary"
      class="mb-3"
    />

    <VRow>
      <VCol
        v-for="type in types"
        :key="type.id"
        cols="12"
        md="6"
        lg="4"
      >
        <VCard
          variant="flat"
          class="border h-100"
        >
          <VCardItem>
            <template #title>
              <div class="d-flex align-center gap-2">
                <span class="text-subtitle-1 font-weight-medium">{{ type.name }}</span>
                <VChip
                  v-if="!type.is_active"
                  size="x-small"
                  color="grey-500"
                  label
                >
                  <span>Inactive</span>
                </VChip>
                <VChip
                  v-if="type.is_system"
                  size="x-small"
                  color="info"
                  label
                >
                  <span>System</span>
                </VChip>
              </div>
            </template>
            <template #subtitle>
              <span class="text-body-2 text-medium-emphasis">
                {{ documentTierTitle(type.tier) }} · prefix {{ type.code_prefix }}
              </span>
            </template>
          </VCardItem>

          <VCardText>
            <div class="text-body-2 text-medium-emphasis mb-2">
              Review: {{ reviewFrequencyTitle(type.default_review_frequency) }}
            </div>
            <VChip
              :color="type.has_approval_route ? 'success' : 'warning'"
              size="x-small"
              label
            >
              <span>
                {{ type.has_approval_route
                  ? `${type.approval_route.length}-step route`
                  : 'No approval route' }}
              </span>
            </VChip>
          </VCardText>

          <VCardActions v-if="can('configure', 'quality.document')">
            <VBtn
              size="small"
              variant="text"
              @click="openEdit(type)"
            >
              Edit
            </VBtn>
            <VBtn
              v-if="!type.is_system"
              size="small"
              variant="text"
              color="error"
              @click="confirmDelete(type)"
            >
              Delete
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>

    <DocumentTypeDrawer
      v-model:is-drawer-open="isDrawerOpen"
      :document-type="editing"
      @saved="fetchTypes"
    />

    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmOpen"
      :confirmation-question="`Delete the document type ${deleting?.name}?`"
      confirm-title="Delete"
      confirm-msg="A type that already has documents cannot be deleted — deactivate it instead."
      @confirm="doDelete"
    />
  </div>
</template>
